import { useEffect, useState, useCallback } from 'react';
import { categoriesApi } from '@/lib/api/arrex/catalog';
import { settingsApi } from '@/lib/api/arrex/settings';
import { useApp } from '@/contexts/AppContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ToggleField } from '@/components/ui/toggle-field';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, Trash2, Save, RotateCcw, Loader2 } from 'lucide-react';
import { localized, rowImg } from "@/lib/format";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface HomeCollectionRow {
  /** row id in new_arrivals table — undefined for rows not yet persisted */
  id?: string;
  category_id: string;
  is_visible: boolean;
  sort_order: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
async function fetchAllCategories(): Promise<any[]> {
  const res = await categoriesApi.adminList();
  return res.items || [];
}

function listEqual(a: HomeCollectionRow[], b: HomeCollectionRow[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((r, i) =>
    r.category_id === b[i].category_id &&
    r.is_visible === b[i].is_visible,
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function AdminHomeCollections({ embedded = false }: { embedded?: boolean }) {
  const { lang } = useApp();
  const { t } = useTranslation();

  const [categories, setcategories] = useState<any[]>([]);
  /** Authoritative list fetched from the server */
  const [saved, setSaved] = useState<HomeCollectionRow[]>([]);
  /** Local working copy — all edits happen here first */
  const [draft, setDraft] = useState<HomeCollectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // -------------------------------------------------------------------------
  // Load
  // -------------------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ps, na] = await Promise.all([
        fetchAllCategories(),
        settingsApi.adminHomeCollections(),
      ]);
      setcategories(ps);
      const rows: HomeCollectionRow[] = (na.items || []).map((r: any) => ({
        id: r.id,
        category_id: r.category_id,
        is_visible: r.is_visible ?? true,
        sort_order: r.sort_order ?? 0,
      }));
      setSaved(rows);
      setDraft(rows);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // -------------------------------------------------------------------------
  // Draft helpers
  // -------------------------------------------------------------------------
  const isDirty = !listEqual(draft, saved);
  const inDraft = (pid: string) => draft.some(r => r.category_id === pid);

  /** Toggle a category in/out of the draft list */
  const toggle = (pid: string) => {
    setDraft(prev => {
      if (prev.some(r => r.category_id === pid)) {
        // Remove from draft
        return prev.filter(r => r.category_id !== pid);
      } else {
        // Add to draft at the end
        return [
          ...prev,
          { category_id: pid, is_visible: true, sort_order: prev.length },
        ];
      }
    });
  };

  /** Move an item up (-1) or down (+1) within the draft */
  const move = (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    setDraft(prev => {
      if (next < 0 || next >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[next]] = [copy[next], copy[idx]];
      return copy;
    });
  };

  /** Toggle visibility for a draft item */
  const setVisible = (pid: string, v: boolean) => {
    setDraft(prev => prev.map(r => r.category_id === pid ? { ...r, is_visible: v } : r));
  };

  /** Remove from draft (trash icon) */
  const remove = (pid: string) => {
    setDraft(prev => prev.filter(r => r.category_id !== pid));
  };

  /** Discard draft changes */
  const reset = () => setDraft(saved);

  // -------------------------------------------------------------------------
  // Save — reconcile draft vs. saved
  // -------------------------------------------------------------------------
  const save = async () => {
    setSaving(true);
    try {
      const savedIds = new Set(saved.map(r => r.category_id));
      const draftIds = new Set(draft.map(r => r.category_id));

      // 1. Add categories that are in draft but not in saved
      for (const row of draft) {
        if (!savedIds.has(row.category_id)) {
          await settingsApi.addHomeCollection(row.category_id);
        }
      }

      // 2. Remove categories that were in saved but removed from draft
      for (const row of saved) {
        if (!draftIds.has(row.category_id)) {
          if (row.id) {
            await settingsApi.removeHomeCollection(row.id);
          } else {
            await settingsApi.removeHomeCollectionByCategory(row.category_id);
          }
        }
      }

      // 3. After adds/removes, reload to get fresh IDs for newly added rows
      const freshNa = await settingsApi.adminHomeCollections();
      const freshRows: HomeCollectionRow[] = (freshNa.items || []).map((r: any) => ({
        id: r.id,
        category_id: r.category_id,
        is_visible: r.is_visible ?? true,
        sort_order: r.sort_order ?? 0,
      }));

      // 4. Build the reorder array matching current draft order
      const draftOrderedPids = draft.map(r => r.category_id);
      const reorderIds: string[] = draftOrderedPids
        .map(pid => freshRows.find(r => r.category_id === pid)?.id)
        .filter(Boolean) as string[];

      if (reorderIds.length > 0) {
        await settingsApi.reorderHomeCollections(reorderIds);
      }

      // 5. Sync visibility for items that changed
      for (const dRow of draft) {
        const freshRow = freshRows.find(r => r.category_id === dRow.category_id);
        if (!freshRow) continue;
        const savedRow = saved.find(r => r.category_id === dRow.category_id);
        if (savedRow && savedRow.is_visible !== dRow.is_visible) {
          await settingsApi.updateHomeCollection(freshRow.id!, { is_visible: dRow.is_visible });
        } else if (!savedRow) {
          // Newly added row — set its visibility if it's not the default true
          if (!dRow.is_visible) {
            await settingsApi.updateHomeCollection(freshRow.id!, { is_visible: false });
          }
        }
      }

      toast.success('Changes saved successfully');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------
  const categoryById = (pid: string) => categories.find(p => p.id === pid);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Header — hidden when embedded inside another page */}
      {!embedded && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold">{t('admin.nav.collections')}</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              {t('admin.curated.collectionsSubtitle')}
            </p>
          </div>

          {isDirty && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={reset} disabled={saving}>
                <RotateCcw className="w-4 h-4 mr-1" />
                {t('admin.common.discard')}
              </Button>
              <Button size="sm" onClick={save} disabled={saving}>
                {saving
                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  : <Save className="w-4 h-4 mr-1" />}
                {t('admin.common.saveChanges')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* When embedded, show save/discard inline at the top if dirty */}
      {embedded && isDirty && (
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={reset} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-1" />
            {t('admin.common.discard')}
          </Button>
          <Button size="sm" onClick={save} disabled={saving}>
            {saving
              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              : <Save className="w-4 h-4 mr-1" />}
            {t('admin.common.saveChanges')}
          </Button>
        </div>
      )}

      {/* Unsaved changes banner */}
      {isDirty && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-700 dark:text-amber-300">
          {t('admin.common.unsaved')}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* ---------------------------------------------------------------- */}
        {/* LEFT — Curated List                                              */}
        {/* ---------------------------------------------------------------- */}
        <div className="surface-card rounded-lg p-4">
          <h2 className="font-display text-lg mb-3">
            {t('admin.curated.list')} ({draft.length})
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('admin.common.loading')}
            </div>
          ) : (
            <div className="space-y-2">
              {draft.map((row, idx) => {
                const p = categoryById(row.category_id);
                return (
                  <div
                    key={row.category_id}
                    className="rounded border border-border p-2 flex items-center justify-between gap-3 min-w-0"
                  >
                    {/* Left: image + name */}
                    <div className="flex items-center gap-2 min-w-0">
                      {rowImg(p) && (
                        <img
                          src={rowImg(p)}
                          className="w-8 h-8 rounded object-cover flex-shrink-0"
                          alt=""
                        />
                      )}
                      <span className="text-sm font-medium leading-tight truncate">
                        {p ? localized(p, 'name', lang) : <span className="text-muted-foreground">—</span>}
                      </span>
                    </div>

                    {/* Right: controls (toggle, status label, reorder, delete) */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <ToggleField
                        checked={row.is_visible}
                        onCheckedChange={(v) => setVisible(row.category_id, v)}
                        labelPosition="end"
                        size="sm"
                        labelClassName="hidden sm:inline"
                      />
                      <div className="h-4 w-px bg-border mx-1" />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        title={t('admin.common.moveUp')}
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => move(idx, 1)}
                        disabled={idx === draft.length - 1}
                        title={t('admin.common.moveDown')}
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => remove(row.category_id)}
                        title={t('admin.common.remove')}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                );
              })}

              {draft.length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  {t('admin.curated.emptyCategories')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* RIGHT — All Categories picker                                      */}
        {/* ---------------------------------------------------------------- */}
        <div className="surface-card rounded-lg p-4">
          <h2 className="font-display text-lg mb-3">{t('admin.curated.allCategories')}</h2>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {categories.map(p => {
              const checked = inDraft(p.id);
              return (
                <label
                  key={p.id}
                  className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors select-none ${
                    checked ? 'bg-primary/10' : 'hover:bg-muted/50'
                  }`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(p.id)}
                    className="flex-shrink-0"
                  />
                  {rowImg(p) && (
                    <img
                      src={rowImg(p)}
                      className="w-8 h-8 rounded object-cover flex-shrink-0"
                      alt=""
                    />
                  )}
                  <span className="text-sm leading-snug break-words min-w-0">
                    {localized(p, 'name', lang)}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
